import { useEffect, useRef, useState } from 'react';

import type { RJSFSchema } from '@rjsf/utils';

import { PROPERTY_CHANNEL_MANAGERS } from '~/consts/reservation';

import ReservationService from '~/services/ReservationService';

import getErrorMessage from '~/utils/getErrorMessage';

import { alwaysActionable, noResetNeeded } from '../types';

import { buildTaxesAndFeesUpdatePayload, mapSavedTaxFee, mapTaxSettings } from './formData';
import type { SavedPropertyTaxes, TaxFeesFormData, TaxesAndFeesUpdatePayload } from './formData';
import { getTaxFeePrefillItems, mergeTaxFeeItems, withDefaultTaxFeeProductType } from './taxRules';

export interface PrefillChanges {
  added: Array<string>;
  changed: Array<string>;
  removed: Array<string>;
}

export interface UseTaxesFeesStepParams {
  isTaxesAndFeesStep: boolean;
  propertyId: string | undefined;
}

/**
 * Owns the Taxes & Fees step (7): loading the property's current taxes/fees plus its create
 * schema, merging in this location's prefill rules, and holding the CM's edited values as a
 * draft. The step's own presentational component unmounts once the CM navigates away, so this
 * (wizard-level) hook is what keeps the draft alive until the final Submission step applies it.
 */
export function useTaxesFeesStep({ isTaxesAndFeesStep, propertyId }: UseTaxesFeesStepParams) {
  const [schema, setSchema] = useState<RJSFSchema | null>(null);
  const [formData, setFormData] = useState<TaxFeesFormData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [prefillChanges, setPrefillChanges] = useState<PrefillChanges | null>(null);
  const [taxesFeesDraft, setTaxesFeesDraft] = useState<TaxesAndFeesUpdatePayload | null>(null);
  const loadedPropertyIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!isTaxesAndFeesStep || !propertyId) {
      return;
    }

    // Re-entering this step (Next -> Back -> Next) re-fires this effect with the same
    // propertyId -- skip the reload so it can't overwrite the CM's edited taxesFeesDraft.
    if (loadedPropertyIdRef.current === propertyId) {
      return;
    }

    setIsLoading(true);
    setErrorMessage(null);
    setPrefillChanges(null);

    let ignore = false;

    Promise.all([
      ReservationService.getProperty(propertyId),
      ReservationService.getPropertyTaxesAndFeesSchema(propertyId),
    ])
      .then(([propertyData, schemaData]) => {
        if (ignore) {
          return;
        }

        const loadedProperty = propertyData.result as SavedPropertyTaxes;
        const existingItems = (loadedProperty.taxes_and_fees ?? []).map(mapSavedTaxFee);
        const prefillItems = getTaxFeePrefillItems(loadedProperty.location_subheading, loadedProperty.location_heading);
        const merged = mergeTaxFeeItems(existingItems, prefillItems);
        const loadedSchema = schemaData.put.body.schema;
        const {
          all_taxes_and_fees_as_non_commssionable,
          channel_manager_reservation_rate,
          commission_statement,
          exclude_flash_pay_at_property,
          gstin,
          include_tax_amount_into_before_amount,
          send_daily_override_rates,
          taxes_and_fees,
          taxes_payable_at_property,
        } = loadedSchema.properties;

        setSchema({
          type: 'object',
          properties: {
            commission_statement,
            gstin,
            taxes_payable_at_property,
            all_taxes_and_fees_as_non_commssionable,
            ...(loadedProperty.channel_manager === PROPERTY_CHANNEL_MANAGERS.derbysoft
              ? { include_tax_amount_into_before_amount }
              : {}),
            exclude_flash_pay_at_property,
            send_daily_override_rates,
            channel_manager_reservation_rate,
            taxes_and_fees: withDefaultTaxFeeProductType(taxes_and_fees),
          },
          dependencies: loadedSchema.dependencies,
        });
        setFormData({
          ...mapTaxSettings(loadedProperty),
          taxes_and_fees: merged.items,
        });

        setPrefillChanges({ added: merged.added, changed: merged.changed, removed: merged.removed });
        loadedPropertyIdRef.current = propertyId;
      })
      .catch((error) => {
        if (!ignore) {
          setErrorMessage(getErrorMessage(error));
        }
      })
      .finally(() => {
        if (!ignore) {
          setIsLoading(false);
        }
      });

    return () => {
      ignore = true;
    };
  }, [isTaxesAndFeesStep, propertyId]);

  function handleChange(nextFormData: TaxFeesFormData) {
    setErrorMessage(null);
    setFormData(nextFormData);
  }

  // Shapes the submitted form values into the update payload and holds them as a draft --
  // applying them for real is the Submission step's job, once the whole offer is confirmed.
  function saveDraft(submittedFormData: TaxFeesFormData) {
    setTaxesFeesDraft(buildTaxesAndFeesUpdatePayload(submittedFormData));
  }

  function reset() {
    setSchema(null);
    setFormData(null);
    setIsLoading(false);
    setErrorMessage(null);
    setPrefillChanges(null);
    setTaxesFeesDraft(null);
    loadedPropertyIdRef.current = undefined;
  }

  return {
    errorMessage,
    formData,
    handleChange,
    isLoading,
    prefillChanges,
    isSubmissionFailureActionable: alwaysActionable,
    resetAfterFailedSubmission: noResetNeeded,
    reset,
    saveDraft,
    schema,
    setErrorMessage,
    taxesFeesDraft,
  };
}
